import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import {
  MediaItemCardWithActions,
  MediaItemCardWithActionsProps,
} from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardWithActions/MediaItemCardWithActions";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { Spacer } from "@coworker/reusable/components/Widgets";
import { useMemo } from "react";
import { BackupProductSection } from "../BackupProduct/BackupProductSection";
import { joinClassNames } from "../utils";
import productCardStyles from "./product-card.module.css";
import {
  ProductCardActions,
  ProductCardActionsProps,
} from "./ProductCardActions";

type ProductCardProps<T extends Article | AggregatedArticle> = Pick<
  MediaItemCardWithActionsProps<T>,
  "item" | "article" | "badges" | "saleStartDate"
> &
  ProductCardActionsProps & {
    backupProduct?: Item | null;
    onSelectBackupProduct?: () => void;
    onRemoveBackupProduct?: () => void;
  };

export const ProductCard = <T extends Article | AggregatedArticle>({
  item,
  article,
  badges,
  saleStartDate,
  onDelete,
  onEdit,
  backupProduct,
  onSelectBackupProduct,
  onRemoveBackupProduct,
}: ProductCardProps<T>) => {
  // TODO: rewrite it when BE is providing a dedicated field for main product
  const productIsMainProduct = useMemo(
    () => (badges || []).some(({ text }) => text === "Main product"),
    [badges]
  );

  const productCardActionsAreEnabled = Boolean(onDelete || onEdit);
  const backupProductIsEnabled =
    productIsMainProduct && Boolean(onSelectBackupProduct);
  const renderActionsAreEnabled =
    productCardActionsAreEnabled || backupProductIsEnabled;

  return (
    <MediaItemCardWithActions
      className={joinClassNames(
        productCardStyles["productCard"],
        productIsMainProduct && productCardStyles["productCard_mainProduct"]
      )}
      item={item}
      article={article}
      badges={badges}
      saleStartDate={saleStartDate}
      edsData={null}
      itemCardRenderType={ItemCardRenderType.static}
      mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
      renderActions={
        renderActionsAreEnabled
          ? () => (
              <>
                <Spacer />

                {productCardActionsAreEnabled && (
                  <ProductCardActions onDelete={onDelete} onEdit={onEdit} />
                )}

                {backupProductIsEnabled && (
                  <BackupProductSection
                    backupProduct={backupProduct}
                    onSelect={() => onSelectBackupProduct?.()}
                    onRemove={() => onRemoveBackupProduct?.()}
                  />
                )}
              </>
            )
          : undefined
      }
    />
  );
};
